import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Divider,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
} from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PetsIcon from '@mui/icons-material/Pets';
import PestControlIcon from '@mui/icons-material/PestControl';
import ApiIcon from '@mui/icons-material/Api';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import SmsIcon from '@mui/icons-material/Sms';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import markerRetina from 'leaflet/dist/images/marker-icon-2x.png';
import { useAuth } from '../contexts/AuthContext';
import { useBusiness } from '../contexts/BusinessContext';
import OptionCard from '../components/onboarding/OptionCard';
import { DeviceType, InferenceModel } from '../components/devices/types';

// Same default-icon fix as SetDeviceLocationModal.tsx — Leaflet's marker
// icon paths break under webpack's asset handling without this.
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerRetina,
  shadowUrl: markerShadow,
});

// Same fallback used by BusinessMapTab.tsx when there's nothing yet to
// center on (no business address geocoding happens at onboarding time).
const AUSTRALIA_CENTER: [number, number] = [-23.698, 133.8807];

const ClickToPlace: React.FC<{ onPick: (lat: number, lng: number) => void }> = ({ onPick }) => {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

interface UserInfo {
  id: string;
  emailConfirmed: boolean;
  phoneNumber: string | null;
  phoneNumberConfirmed: boolean;
}

interface SubscriptionType {
  id: string;
  code: string;
  name: string;
  description: string | null;
  price: number;
  isTrial: boolean;
  planFamily: string;
  includesHardwareRental: boolean;
  trapDeviceTypeId: string | null;
  includesApiAccess: boolean;
  archivedDate: string | null;
}

interface CameraDraft {
  key: string;
  name: string;
  deviceTypeId: string;
  inferenceModelId: string;
  classIndexes: Set<number>;
}

const AU_MOBILE_REGEX = /^(\+?61|0)4\d{8}$/;

const FAMILY_META: Record<string, { label: string; caption: string; icon: SvgIconComponent }> = {
  INFERENCE_NOTIFY: {
    label: 'Inference + Notification',
    caption: 'Use your own camera — we detect and text you.',
    icon: NotificationsActiveIcon,
  },
  PIG_TRAP_RENTAL: {
    label: 'Pig Trap Rental',
    caption: 'Rent a pig trap with detection + SMS included.',
    icon: PetsIcon,
  },
  FOXCAT_TRAP_RENTAL: {
    label: 'Fox/Cat Trap Rental',
    caption: 'Rent a fox/cat trap with detection + SMS included.',
    icon: PestControlIcon,
  },
  API_ACCESS: {
    label: 'API Access',
    caption: 'Submit images to our classification API yourself.',
    icon: ApiIcon,
  },
};

const CAMERA_BASED_FAMILIES = new Set(['INFERENCE_NOTIFY', 'PIG_TRAP_RENTAL', 'FOXCAT_TRAP_RENTAL']);

// Survives the trip out to click an email confirmation link (which drops
// all in-memory React state) — see readme_files/20260912-subscription-rework.
// ConfirmEmail.tsx reads this same key to route back into this wizard.
const PENDING_PLAN_KEY = 'dmo_pending_onboarding_plan';

const newCamera = (index: number): CameraDraft => ({
  key: `cam-${index}`,
  name: `Camera ${index}`,
  deviceTypeId: '',
  inferenceModelId: '',
  classIndexes: new Set(),
});

const GetStarted: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { setSelectedBusinessId, refreshBusinesses } = useBusiness();
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [planTypes, setPlanTypes] = useState<SubscriptionType[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [inferenceModels, setInferenceModels] = useState<InferenceModel[]>([]);

  // Verify step
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);
  const [phoneCodeInput, setPhoneCodeInput] = useState('');
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifyNotice, setVerifyNotice] = useState<string | null>(null);

  // Business step
  const [businessName, setBusinessName] = useState('');
  const [abn, setAbn] = useState('');
  const [showAddress, setShowAddress] = useState(false);
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [stateField, setStateField] = useState('');
  const [postCode, setPostCode] = useState('');
  const [country, setCountry] = useState('Australia');
  const [businessBusy, setBusinessBusy] = useState(false);
  const [businessError, setBusinessError] = useState<string | null>(null);
  const [business, setBusiness] = useState<{ id: string; name: string } | null>(null);

  // Plan step
  const [selectedFamilies, setSelectedFamilies] = useState<Set<string>>(new Set());
  const [subscribeBusy, setSubscribeBusy] = useState(false);
  const [subscribeError, setSubscribeError] = useState<string | null>(null);
  const [restoredFromPending, setRestoredFromPending] = useState(false);
  const pendingRestoreAttempted = useRef(false);
  const activeIndexRestored = useRef(false);
  const autoFinishAttempted = useRef(false);

  // Devices step
  const [numCameras, setNumCameras] = useState<1 | 2>(1);
  const [parentName, setParentName] = useState('My Trap');
  const [cameras, setCameras] = useState<CameraDraft[]>([newCamera(1)]);
  const [devicesBusy, setDevicesBusy] = useState(false);
  const [devicesError, setDevicesError] = useState<string | null>(null);
  const [smsOwnerDeviceId, setSmsOwnerDeviceId] = useState<string | null>(null);
  // One location per trap/camera unit (the top-level device), not per
  // individual camera — matches SetDeviceLocationModal's topLevelOnly
  // fetch elsewhere in the app. Required: DevicesController's dashboard
  // map endpoint only returns devices with a saved lat/lng, so a camera
  // created here without one simply wouldn't appear on the dashboard.
  const [deviceLocation, setDeviceLocation] = useState<[number, number] | null>(null);

  // SMS step
  const [smsNumbers, setSmsNumbers] = useState<string[]>([]);
  const [smsNumberInput, setSmsNumberInput] = useState('');
  const [smsMin, setSmsMin] = useState('1');
  const [smsCooldown, setSmsCooldown] = useState('60');
  const [smsClassThresholds, setSmsClassThresholds] = useState<Map<string, string>>(new Map());
  const [smsBusy, setSmsBusy] = useState(false);
  const [smsError, setSmsError] = useState<string | null>(null);

  // API key step
  const [apiKeyResult, setApiKeyResult] = useState<{ apiKey: string; label: string } | null>(null);
  const [apiKeyBusy, setApiKeyBusy] = useState(false);
  const [apiKeyError, setApiKeyError] = useState<string | null>(null);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch('/api/users/me', { headers: authHeader }).then((r) => (r.ok ? r.json() : null)),
      fetch('/api/subscription-types', { headers: authHeader }).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/device-types', { headers: authHeader }).then((r) => (r.ok ? r.json() : [])),
    ])
      .then(([me, types, dTypes]: [UserInfo | null, SubscriptionType[], DeviceType[]]) => {
        setUserInfo(me);
        setPlanTypes(types.filter((t) => !t.archivedDate));
        setDeviceTypes(dTypes);
        if (me?.phoneNumber) setPhoneInput(me.phoneNumber);
      })
      .catch(() => setLoadError('Failed to load your account details.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const needsCameraSetup = Array.from(selectedFamilies).some((f) => CAMERA_BASED_FAMILIES.has(f));
  const needsApiKey = selectedFamilies.has('API_ACCESS');

  const steps = useMemo(() => {
    const s: { key: string; label: string }[] = [
      { key: 'business', label: 'Business' },
      { key: 'plan', label: 'Plan' },
      { key: 'verify', label: 'Verify' },
    ];
    if (needsCameraSetup) {
      s.push({ key: 'devices', label: 'Camera setup' });
      s.push({ key: 'sms', label: 'SMS alerts' });
    }
    if (needsApiKey) {
      s.push({ key: 'apiKey', label: 'API access' });
    }
    s.push({ key: 'done', label: 'Done' });
    return s;
  }, [needsCameraSetup, needsApiKey]);

  const stepKey = steps[activeIndex]?.key ?? 'verify';
  const goNext = () => setActiveIndex((i) => Math.min(i + 1, steps.length - 1));
  const goBack = () => setActiveIndex((i) => Math.max(i - 1, 0));

  const isVerified = Boolean(userInfo?.emailConfirmed && userInfo?.phoneNumberConfirmed);

  const refreshUserInfo = async () => {
    const res = await fetch('/api/users/me', { headers: authHeader });
    if (res.ok) setUserInfo(await res.json());
  };

  // Restore a business+plan chosen before the user left to click an email
  // confirmation link — the confirm-email page redirects back here rather
  // than to a dead end, but the redirect is a full page load, so all
  // in-memory state (including activeIndex) is gone and must be rebuilt
  // from PENDING_PLAN_KEY.
  useEffect(() => {
    if (loading || pendingRestoreAttempted.current) return;
    pendingRestoreAttempted.current = true;
    try {
      const raw = localStorage.getItem(PENDING_PLAN_KEY);
      if (!raw) return;
      const pending = JSON.parse(raw) as {
        userId: string;
        businessId: string;
        businessName: string;
        selectedFamilies: string[];
      };
      if (pending.userId !== auth.user?.id) return;
      setBusiness({ id: pending.businessId, name: pending.businessName });
      setSelectedFamilies(new Set(pending.selectedFamilies));
      setRestoredFromPending(true);
    } catch {
      // Corrupt/unreadable entry — user just starts the wizard fresh.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Jump straight to the Verify step once a pending selection is restored,
  // rather than making the user re-enter business/plan they already chose.
  useEffect(() => {
    if (!restoredFromPending || activeIndexRestored.current) return;
    const verifyIndex = steps.findIndex((s) => s.key === 'verify');
    if (verifyIndex >= 0) {
      setActiveIndex(verifyIndex);
      activeIndexRestored.current = true;
    }
  }, [restoredFromPending, steps]);

  // ── Verify step ──────────────────────────────────────────────────────────
  const handleResendEmail = async () => {
    setVerifyBusy(true);
    setVerifyError(null);
    setVerifyNotice(null);
    try {
      const res = await fetch('/api/users/me/email/send-confirmation', { method: 'POST', headers: authHeader });
      if (!res.ok) throw new Error('Failed to resend confirmation email.');
      setVerifyNotice('Confirmation email sent — click the link in it, then refresh below.');
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : 'Failed to resend confirmation email.');
    } finally {
      setVerifyBusy(false);
    }
  };

  const handleSendPhoneCode = async () => {
    setVerifyBusy(true);
    setVerifyError(null);
    try {
      const res = await fetch('/api/users/me/phone/send-code', {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ phoneNumber: phoneInput }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to send verification code.');
      setPhoneCodeSent(true);
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : 'Failed to send verification code.');
    } finally {
      setVerifyBusy(false);
    }
  };

  const handleVerifyPhoneCode = async () => {
    setVerifyBusy(true);
    setVerifyError(null);
    try {
      const res = await fetch('/api/users/me/phone/verify-code', {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: phoneCodeInput }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Invalid code.');
      await refreshUserInfo();
    } catch (err) {
      setVerifyError(err instanceof Error ? err.message : 'Invalid code.');
    } finally {
      setVerifyBusy(false);
    }
  };

  // ── Business step ────────────────────────────────────────────────────────
  // Farm boundary creation deliberately isn't part of onboarding — it can
  // always be drawn later from the map tools, and asking for it here just
  // added friction to a step users are already trying to get through.
  const handleCreateBusiness = async () => {
    if (abn && !/^\d{11}$/.test(abn)) {
      setBusinessError('ABN must be exactly 11 digits');
      return;
    }
    setBusinessBusy(true);
    setBusinessError(null);
    try {
      const res = await fetch('/api/businesses/self-register', {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: businessName,
          abn,
          primaryUserId: auth.user?.id,
          address:
            street || city
              ? { street, city, state: stateField, postCode, country }
              : null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to create business');

      const businessId: string = data.businessId;
      setBusiness({ id: businessId, name: businessName });

      // Re-fetch profile now that BusinessUser access exists — this is what
      // backfills AppUser.DefaultBusinessId (see UserProfileController.EnsureDefaultsAsync).
      await refreshUserInfo();
      await refreshBusinesses();
      setSelectedBusinessId(businessId);

      goNext();
    } catch (err) {
      setBusinessError(err instanceof Error ? err.message : 'Failed to create business');
    } finally {
      setBusinessBusy(false);
    }
  };

  // ── Plan step ────────────────────────────────────────────────────────────
  const toggleFamily = (family: string) => {
    setSelectedFamilies((prev) => {
      const next = new Set(prev);
      if (next.has(family)) next.delete(family);
      else next.add(family);
      return next;
    });
  };

  const trialPlanForFamily = (family: string) => planTypes.find((t) => t.planFamily === family && t.isTrial);
  const availableFamilies = Object.keys(FAMILY_META).filter((f) => trialPlanForFamily(f));

  // Plan selection only — doesn't call the subscribe API. Starting a trial
  // requires confirmed email+phone server-side (BusinessSubscriptionsController),
  // and Verify now comes after this step, so the actual subscribe call is
  // deferred to handleFinishVerification below.
  const handleSelectPlan = () => {
    if (!business) return;
    try {
      localStorage.setItem(
        PENDING_PLAN_KEY,
        JSON.stringify({
          userId: auth.user?.id,
          businessId: business.id,
          businessName: business.name,
          selectedFamilies: Array.from(selectedFamilies),
        })
      );
    } catch {
      // localStorage unavailable — the auto-resume-after-email convenience
      // just won't kick in; verifying and clicking Continue still works.
    }
    goNext();
  };

  // ── Verify step (continued) — fires the deferred subscribe once verified ──
  const handleFinishVerification = async () => {
    if (!business) {
      goNext();
      return;
    }
    setSubscribeBusy(true);
    setSubscribeError(null);
    const errors: string[] = [];
    for (const family of Array.from(selectedFamilies)) {
      const plan = trialPlanForFamily(family);
      if (!plan) continue;
      try {
        const res = await fetch(`/api/businesses/${business.id}/subscriptions`, {
          method: 'POST',
          headers: { ...authHeader, 'Content-Type': 'application/json' },
          body: JSON.stringify({ subscriptionTypeId: plan.id }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) errors.push(`${FAMILY_META[family]?.label ?? family}: ${data.error || 'failed to start'}`);
      } catch {
        errors.push(`${FAMILY_META[family]?.label ?? family}: failed to start`);
      }
    }
    setSubscribeBusy(false);
    if (errors.length > 0) {
      setSubscribeError(errors.join(' — '));
      return;
    }

    try {
      localStorage.removeItem(PENDING_PLAN_KEY);
    } catch {
      // ignore
    }

    if (Array.from(selectedFamilies).some((f) => CAMERA_BASED_FAMILIES.has(f))) {
      try {
        const modelsRes = await fetch(`/api/businesses/${business.id}/allowed-inference-models`, { headers: authHeader });
        if (modelsRes.ok) setInferenceModels(await modelsRes.json());
      } catch {
        // non-fatal — the model picker will just come up empty
      }
      // /api/device-types was already fetched once at mount (see the initial
      // useEffect), but at that point there was no subscription yet, so the
      // subscription-gate rejected it with 402 and deviceTypes was left an
      // empty array for the rest of the session — leaving the Camera
      // hardware picker on the next step with nothing to show. Refetch now
      // that a subscription actually exists.
      try {
        const deviceTypesRes = await fetch('/api/device-types', { headers: authHeader });
        if (deviceTypesRes.ok) setDeviceTypes(await deviceTypesRes.json());
      } catch {
        // non-fatal — the hardware picker will just come up empty
      }
    }

    goNext();
  };

  // Auto-completes the flow on return from the email link, without waiting
  // for a manual "Continue" click — only for a session restored from
  // PENDING_PLAN_KEY, not the normal in-session path (where the user just
  // clicked Continue on Plan a moment ago and clicking it again on Verify
  // is the expected, unsurprising next action).
  useEffect(() => {
    if (!restoredFromPending || autoFinishAttempted.current) return;
    if (stepKey === 'verify' && isVerified) {
      autoFinishAttempted.current = true;
      handleFinishVerification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredFromPending, stepKey, isVerified]);

  // ── Devices step ─────────────────────────────────────────────────────────
  const cameraDeviceTypes = deviceTypes.filter((dt) => dt.deviceCategory === 'Camera');
  const hardwareFamily = Array.from(selectedFamilies).find((f) => f === 'PIG_TRAP_RENTAL' || f === 'FOXCAT_TRAP_RENTAL');
  const hardwarePlan = hardwareFamily ? trialPlanForFamily(hardwareFamily) : undefined;
  const trapDeviceTypeId = hardwarePlan?.trapDeviceTypeId ?? null;

  const handleNumCamerasChange = (n: 1 | 2) => {
    setNumCameras(n);
    setCameras((prev) => {
      const next = [...prev];
      while (next.length < n) next.push(newCamera(next.length + 1));
      return next.slice(0, n);
    });
  };

  const updateCamera = (key: string, patch: Partial<CameraDraft>) => {
    setCameras((prev) => prev.map((c) => (c.key === key ? { ...c, ...patch } : c)));
  };

  const toggleCameraClass = (key: string, classIndex: number) => {
    setCameras((prev) =>
      prev.map((c) => {
        if (c.key !== key) return c;
        const next = new Set(c.classIndexes);
        if (next.has(classIndex)) next.delete(classIndex);
        else next.add(classIndex);
        return { ...c, classIndexes: next };
      })
    );
  };

  const handleCreateDevices = async () => {
    if (!business) return;
    if (cameras.some((c) => !c.name.trim() || !c.deviceTypeId)) {
      setDevicesError('Every camera needs a name and a hardware type.');
      return;
    }
    setDevicesBusy(true);
    setDevicesError(null);
    try {
      let parentId: string | null = null;
      if (numCameras === 2) {
        const parentDeviceTypeId = trapDeviceTypeId || cameras[0].deviceTypeId;
        const res = await fetch(`/api/devices?businessId=${business.id}`, {
          method: 'POST',
          headers: { ...authHeader, 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: parentName, deviceTypeId: parentDeviceTypeId, inferenceEnabled: false }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Failed to create device');
        parentId = data.id;
      }

      const cameraIds: string[] = [];
      for (const camera of cameras) {
        const classIndexes = Array.from(camera.classIndexes);
        const res = await fetch(parentId ? '/api/devices' : `/api/devices?businessId=${business.id}`, {
          method: 'POST',
          headers: { ...authHeader, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: camera.name,
            deviceTypeId: camera.deviceTypeId,
            parentId,
            inferenceEnabled: Boolean(camera.inferenceModelId),
            inferenceModelId: camera.inferenceModelId || null,
            inferenceClasses: classIndexes.length > 0 ? classIndexes.join(',') : null,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Failed to create camera');
        cameraIds.push(data.id);
      }

      const topLevelDeviceId = parentId || cameraIds[0];
      if (deviceLocation) {
        await upsertSetting(topLevelDeviceId, 'device.latitude', String(deviceLocation[0]));
        await upsertSetting(topLevelDeviceId, 'device.longitude', String(deviceLocation[1]));
      }

      setSmsOwnerDeviceId(topLevelDeviceId);
      goNext();
    } catch (err) {
      setDevicesError(err instanceof Error ? err.message : 'Failed to create devices');
    } finally {
      setDevicesBusy(false);
    }
  };

  // ── SMS step ─────────────────────────────────────────────────────────────
  const availableClasses = useMemo(() => {
    const byName = new Map<string, string>();
    for (const camera of cameras) {
      const model = inferenceModels.find((m) => m.id === camera.inferenceModelId);
      if (!model) continue;
      for (const idx of Array.from(camera.classIndexes)) {
        const cls = model.classes.find((c) => c.classIndex === idx);
        if (cls) byName.set(cls.className, cls.className);
      }
    }
    return Array.from(byName.keys());
  }, [cameras, inferenceModels]);

  const handleAddSmsNumber = () => {
    const cleaned = smsNumberInput.replace(/\s|-/g, '');
    if (!AU_MOBILE_REGEX.test(cleaned)) {
      setSmsError('Enter a valid Australian mobile number (e.g. 0412 345 678)');
      return;
    }
    if (!smsNumbers.includes(cleaned)) setSmsNumbers([...smsNumbers, cleaned]);
    setSmsNumberInput('');
    setSmsError(null);
  };

  const upsertSetting = async (deviceId: string, settingName: string, settingValue: string) => {
    const res = await fetch(`/api/devices/${deviceId}/settings`, {
      method: 'POST',
      headers: { ...authHeader, 'Content-Type': 'application/json' },
      body: JSON.stringify({ settingName, settingValue }),
    });
    if (!res.ok) throw new Error(`Failed to save ${settingName}`);
  };

  const handleSaveSms = async () => {
    if (!smsOwnerDeviceId) return;
    if (smsNumbers.length === 0) {
      setSmsError('Add at least one mobile number.');
      return;
    }
    setSmsBusy(true);
    setSmsError(null);
    try {
      await upsertSetting(smsOwnerDeviceId, 'device.sms.enabled', 'true');
      await upsertSetting(smsOwnerDeviceId, 'device.sms.number', smsNumbers.join(','));
      await upsertSetting(smsOwnerDeviceId, 'device.sms.min', smsMin);
      await upsertSetting(smsOwnerDeviceId, 'device.sms.sendnotice-every-x-minutes', smsCooldown);
      if (smsClassThresholds.size > 0) {
        await upsertSetting(smsOwnerDeviceId, 'device.sms.classes', JSON.stringify(Object.fromEntries(smsClassThresholds)));
      }
      goNext();
    } catch (err) {
      setSmsError(err instanceof Error ? err.message : 'Failed to save SMS settings');
    } finally {
      setSmsBusy(false);
    }
  };

  // ── API key step ─────────────────────────────────────────────────────────
  const handleGenerateApiKey = async () => {
    if (!business) return;
    setApiKeyBusy(true);
    setApiKeyError(null);
    try {
      const res = await fetch(`/api/businesses/${business.id}/api-keys`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: 'Default key' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to generate API key');
      setApiKeyResult({ apiKey: data.apiKey, label: data.label });
    } catch (err) {
      setApiKeyError(err instanceof Error ? err.message : 'Failed to generate API key');
    } finally {
      setApiKeyBusy(false);
    }
  };

  const handleCopyApiKey = async () => {
    if (!apiKeyResult) return;
    try {
      await navigator.clipboard.writeText(apiKeyResult.apiKey);
      setApiKeyCopied(true);
    } catch {
      // clipboard API unavailable — user can still select-and-copy manually
    }
  };

  // ── Continue-button wiring ───────────────────────────────────────────────
  let continueHandler: () => void = goNext;
  let continueDisabled = false;
  let continueBusy = false;

  if (stepKey === 'verify') {
    continueHandler = handleFinishVerification;
    continueDisabled = !isVerified;
    continueBusy = subscribeBusy;
  } else if (stepKey === 'business') {
    continueHandler = handleCreateBusiness;
    continueDisabled = !businessName.trim() || (!!abn && !/^\d{11}$/.test(abn));
    continueBusy = businessBusy;
  } else if (stepKey === 'plan') {
    continueHandler = handleSelectPlan;
    continueDisabled = selectedFamilies.size === 0;
  } else if (stepKey === 'devices') {
    continueHandler = handleCreateDevices;
    continueDisabled = !deviceLocation;
    continueBusy = devicesBusy;
  } else if (stepKey === 'sms') {
    continueHandler = handleSaveSms;
    continueBusy = smsBusy;
  } else if (stepKey === 'apiKey') {
    continueDisabled = !apiKeyResult;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">{loadError}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Let's get you set up
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        A few quick steps and your free trial will be ready to go.
      </Typography>

      <Stepper activeStep={activeIndex} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((s) => (
          <Step key={s.key}>
            <StepLabel>{s.label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 4 } }}>
        {stepKey === 'verify' && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Verify your account
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Last step — confirm your email and mobile number to activate {business?.name}'s plan.
            </Typography>
            {subscribeError && <Alert severity="error" sx={{ mb: 2 }}>{subscribeError}</Alert>}
            {verifyError && <Alert severity="error" sx={{ mb: 2 }}>{verifyError}</Alert>}
            {verifyNotice && <Alert severity="info" sx={{ mb: 2 }}>{verifyNotice}</Alert>}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <MarkEmailReadIcon color={userInfo?.emailConfirmed ? 'success' : 'disabled'} sx={{ fontSize: 32 }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1">Email</Typography>
                <Typography variant="body2" color="text.secondary">
                  {userInfo?.emailConfirmed ? 'Confirmed' : 'Check your inbox for a confirmation link'}
                </Typography>
              </Box>
              {!userInfo?.emailConfirmed && (
                <Button onClick={handleResendEmail} disabled={verifyBusy}>
                  Resend email
                </Button>
              )}
              {!userInfo?.emailConfirmed && (
                <Button variant="outlined" onClick={refreshUserInfo} disabled={verifyBusy}>
                  I've confirmed
                </Button>
              )}
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <PhoneAndroidIcon color={userInfo?.phoneNumberConfirmed ? 'success' : 'disabled'} sx={{ fontSize: 32, mt: 0.5 }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Phone
                </Typography>
                {userInfo?.phoneNumberConfirmed ? (
                  <Typography variant="body2" color="text.secondary">
                    Confirmed
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 360 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Mobile number"
                        value={phoneInput}
                        onChange={(e) => setPhoneInput(e.target.value)}
                        disabled={verifyBusy}
                      />
                      <Button onClick={handleSendPhoneCode} disabled={verifyBusy || !phoneInput} sx={{ whiteSpace: 'nowrap' }}>
                        {phoneCodeSent ? 'Resend code' : 'Send code'}
                      </Button>
                    </Box>
                    {phoneCodeSent && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Verification code"
                          value={phoneCodeInput}
                          onChange={(e) => setPhoneCodeInput(e.target.value)}
                          disabled={verifyBusy}
                        />
                        <Button variant="contained" onClick={handleVerifyPhoneCode} disabled={verifyBusy || !phoneCodeInput}>
                          Verify
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {stepKey === 'business' && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Your business
            </Typography>
            {businessError && <Alert severity="error" sx={{ mb: 2 }}>{businessError}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480 }}>
              <TextField
                label="Business Name"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="ABN (11 digits, optional)"
                value={abn}
                onChange={(e) => setAbn(e.target.value)}
                placeholder="12345678901"
                helperText="Australian Business Number, no spaces — optional"
                inputProps={{ maxLength: 11 }}
                fullWidth
              />
              {!showAddress ? (
                <Link component="button" type="button" onClick={() => setShowAddress(true)} sx={{ textAlign: 'left' }}>
                  + Add business address (optional)
                </Link>
              ) : (
                <>
                  <TextField label="Street" value={street} onChange={(e) => setStreet(e.target.value)} fullWidth />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField label="City" value={city} onChange={(e) => setCity(e.target.value)} fullWidth />
                    <TextField label="State" value={stateField} onChange={(e) => setStateField(e.target.value)} fullWidth />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField label="Post Code" value={postCode} onChange={(e) => setPostCode(e.target.value)} sx={{ width: 150 }} />
                    <TextField label="Country" value={country} onChange={(e) => setCountry(e.target.value)} fullWidth />
                  </Box>
                </>
              )}
            </Box>
          </Box>
        )}

        {stepKey === 'plan' && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Choose your plan
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Pick one or more — each is a free 14-day trial, activated once you verify your account next.
            </Typography>
            <Grid container spacing={2}>
              {availableFamilies.map((family) => {
                const meta = FAMILY_META[family];
                return (
                  <Grid item xs={12} sm={6} key={family}>
                    <OptionCard
                      icon={meta.icon}
                      title={meta.label}
                      caption={meta.caption}
                      selected={selectedFamilies.has(family)}
                      onClick={() => toggleFamily(family)}
                      multiSelect
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {stepKey === 'devices' && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Set up your camera{numCameras > 1 ? 's' : ''}
            </Typography>
            {devicesError && <Alert severity="error" sx={{ mb: 2 }}>{devicesError}</Alert>}

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              How many cameras?
            </Typography>
            <ToggleButtonGroup
              value={numCameras}
              exclusive
              onChange={(_, v) => v && handleNumCamerasChange(v)}
              sx={{ mb: 3 }}
            >
              <ToggleButton value={1}>1 camera</ToggleButton>
              <ToggleButton value={2}>2 cameras (grouped under one device)</ToggleButton>
            </ToggleButtonGroup>

            {numCameras === 2 && (
              <TextField
                label="Device name"
                value={parentName}
                onChange={(e) => setParentName(e.target.value)}
                placeholder="e.g. My Pig Trap"
                fullWidth
                sx={{ mb: 3, maxWidth: 400 }}
              />
            )}

            {cameras.map((camera, i) => (
              <Paper key={camera.key} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Camera {i + 1}
                </Typography>
                <TextField
                  label="Camera name"
                  value={camera.name}
                  onChange={(e) => updateCamera(camera.key, { name: e.target.value })}
                  fullWidth
                  sx={{ mb: 2, maxWidth: 400 }}
                />

                <Typography variant="body2" sx={{ mb: 1 }}>
                  Camera hardware
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {cameraDeviceTypes.map((dt) => (
                    <Grid item xs={6} sm={4} md={3} key={dt.id}>
                      <OptionCard
                        icon={CameraAltIcon}
                        title={dt.name}
                        selected={camera.deviceTypeId === dt.id}
                        onClick={() => updateCamera(camera.key, { deviceTypeId: dt.id })}
                      />
                    </Grid>
                  ))}
                </Grid>

                {inferenceModels.length > 0 && (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Inference model
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {inferenceModels.map((model) => (
                        <Grid item xs={6} sm={4} md={3} key={model.id}>
                          <OptionCard
                            icon={ModelTrainingIcon}
                            title={model.displayName}
                            selected={camera.inferenceModelId === model.id}
                            onClick={() =>
                              updateCamera(camera.key, {
                                inferenceModelId: camera.inferenceModelId === model.id ? '' : model.id,
                                classIndexes: new Set(),
                              })
                            }
                          />
                        </Grid>
                      ))}
                    </Grid>

                    {camera.inferenceModelId && (
                      <>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          What should it detect?
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {inferenceModels
                            .find((m) => m.id === camera.inferenceModelId)
                            ?.classes.map((cls) => (
                              <Chip
                                key={cls.id}
                                label={cls.className}
                                color={camera.classIndexes.has(cls.classIndex) ? 'primary' : 'default'}
                                onClick={() => toggleCameraClass(camera.key, cls.classIndex)}
                              />
                            ))}
                        </Box>
                      </>
                    )}
                  </>
                )}
              </Paper>
            ))}

            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Location
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Click the map to drop a pin where {numCameras > 1 ? 'this trap is' : 'the camera is'} —
              needed so it shows up on your dashboard.
            </Typography>
            <Box sx={{ height: 300, mb: 1, borderRadius: 1, overflow: 'hidden' }}>
              <MapContainer center={deviceLocation ?? AUSTRALIA_CENTER} zoom={deviceLocation ? 13 : 4} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <ClickToPlace onPick={(lat, lng) => setDeviceLocation([lat, lng])} />
                {deviceLocation && <Marker position={deviceLocation} />}
              </MapContainer>
            </Box>
            {!deviceLocation && (
              <Typography variant="caption" color="text.secondary">
                No location set yet — click the map above.
              </Typography>
            )}
          </Box>
        )}

        {stepKey === 'sms' && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              SMS alerts
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Get a text when your camera{numCameras > 1 ? 's detect' : ' detects'} something.
            </Typography>
            {smsError && <Alert severity="error" sx={{ mb: 2 }}>{smsError}</Alert>}

            <Box sx={{ display: 'flex', gap: 1, mb: 2, maxWidth: 400 }}>
              <TextField
                size="small"
                fullWidth
                label="Mobile number"
                value={smsNumberInput}
                onChange={(e) => setSmsNumberInput(e.target.value)}
                placeholder="0412 345 678"
              />
              <Button onClick={handleAddSmsNumber}>Add</Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {smsNumbers.map((n) => (
                <Chip key={n} label={n} onDelete={() => setSmsNumbers(smsNumbers.filter((x) => x !== n))} icon={<SmsIcon />} />
              ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 3, maxWidth: 400 }}>
              <TextField
                label="Minimum count to trigger"
                type="number"
                value={smsMin}
                onChange={(e) => setSmsMin(e.target.value)}
                fullWidth
              />
              <TextField
                label="Cooldown (minutes)"
                type="number"
                value={smsCooldown}
                onChange={(e) => setSmsCooldown(e.target.value)}
                fullWidth
              />
            </Box>

            {availableClasses.length > 0 && (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Optional: how many sightings of each class before texting?
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 400 }}>
                  {availableClasses.map((className) => (
                    <Box key={className} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" sx={{ minWidth: 120 }}>
                        {className}
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={smsClassThresholds.get(className) ?? ''}
                        placeholder={smsMin}
                        onChange={(e) => {
                          const next = new Map(smsClassThresholds);
                          if (e.target.value) next.set(className, e.target.value);
                          else next.delete(className);
                          setSmsClassThresholds(next);
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Box>
        )}

        {stepKey === 'apiKey' && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              API access
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Generate a key to submit images to our classification API from your own systems.
            </Typography>
            {apiKeyError && <Alert severity="error" sx={{ mb: 2 }}>{apiKeyError}</Alert>}

            {!apiKeyResult ? (
              <Button variant="contained" size="large" startIcon={<VpnKeyIcon />} onClick={handleGenerateApiKey} disabled={apiKeyBusy}>
                {apiKeyBusy ? 'Generating…' : 'Generate API key'}
              </Button>
            ) : (
              <Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Save this now — for security, it won't be shown again.
                </Alert>
                <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  <Box sx={{ flexGrow: 1 }}>{apiKeyResult.apiKey}</Box>
                  <IconButton onClick={handleCopyApiKey} size="small">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Paper>
                {apiKeyCopied && (
                  <Typography variant="caption" color="success.main">
                    Copied
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {stepKey === 'done' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              You're all set!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {business?.name} is ready to go on its free trial.
            </Typography>
            <Button variant="contained" size="large" onClick={() => navigate('/')}>
              Go to my dashboard
            </Button>
          </Box>
        )}

        {stepKey !== 'done' && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button onClick={goBack} disabled={activeIndex === 0}>
              Back
            </Button>
            <Button variant="contained" size="large" onClick={continueHandler} disabled={continueDisabled || continueBusy}>
              {continueBusy ? <CircularProgress size={22} /> : 'Continue'}
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default GetStarted;
